import React, { useState, useEffect, useRef } from 'react';
import { 
  FaUniversity, FaPlusCircle, FaTimes, FaSpinner, FaDollarSign, 
  FaMoneyBillWave, FaHistory, FaEllipsisV, FaArrowLeft, FaTrash, 
  FaCheckCircle, FaExclamationTriangle, FaChevronRight, FaLandmark 
} from 'react-icons/fa';
import API_URL from '../config';
import './Banque.css'; 

const Banque = ({ entrepriseId }) => {
  // --- États de Navigation et Banques ---
  const [selectedBanque, setSelectedBanque] = useState(null); 
  const [banques, setBanques] = useState([]); 
  const [showBanqueForm, setShowBanqueForm] = useState(false);
  const [newBanqueNom, setNewBanqueNom] = useState('');

  // --- États du Module de Transfert ---
  const [showForm, setShowForm] = useState(false);
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(new Date().toISOString().substring(0, 10));
  const [libelle, setLibelle] = useState('');
  const [loading, setLoading] = useState(false);
  const [soldeCaisse, setSoldeCaisse] = useState(null);
  const [soldeBanque, setSoldeBanque] = useState(null); 
  const [error, setError] = useState(null);

  // --- États de l'Historique ---
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('main'); 
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // --- État Dialogue ---
  const [customDialog, setCustomDialog] = useState(null);

  // --- Fonctions Dialogue ---
  const showCustomDialog = (type, message, action = null, payload = null) => {
      setCustomDialog({ type, message, action, payload });
  };
  const closeCustomDialog = () => setCustomDialog(null);
  const handleDialogAction = () => {
      if (customDialog.action) customDialog.action(customDialog.payload);
      closeCustomDialog();
  };

  // --- Chargement des Banques ---
  useEffect(() => {
    fetchBanques();
  }, [entrepriseId]);

  // --- Effets pour la banque sélectionnée ---
  useEffect(() => {
    if (selectedBanque) {
      loadSoldesTresorerie();
      loadTransactions();
    }
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedBanque, entrepriseId]);

  // --- API : Liste des Banques ---
  const fetchBanques = async () => {
    try {
      const res = await fetch(`${API_URL}/api/banque/liste?entrepriseId=${entrepriseId}`);
      if (res.ok) {
        const data = await res.json();
        setBanques(data);
      }
    } catch (err) { console.error("Erreur chargement banques:", err); }
  };

  const handleAddBanque = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/banque/creer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entrepriseId, nom: newBanqueNom })
      });
      if (res.ok) {
        showCustomDialog('success', `Le compte ${newBanqueNom} a été ajouté.`);
        setNewBanqueNom('');
        setShowBanqueForm(false);
        fetchBanques();
      } else {
          throw new Error("Erreur lors de la création.");
      }
    } catch (err) { showCustomDialog('error', err.message); }
    finally { setLoading(false); }
  };

  // --- API : Soldes et Transactions ---
  const loadSoldesTresorerie = async () => {
    if (!selectedBanque) return;
    setSoldeCaisse(null); setSoldeBanque(null);
    try {
      const res = await fetch(`${API_URL}/api/banque/soldes-tresorerie?entrepriseId=${entrepriseId}&banqueId=${selectedBanque.id}`); 
      const data = await res.json();
      setSoldeCaisse(parseFloat(data.soldeCaisse) || 0); 
      setSoldeBanque(parseFloat(data.soldeBanque) || 0); 
    } catch (err) { setError("Erreur de chargement des soldes."); }
  };

  const loadTransactions = async () => {
    if (!selectedBanque) return;
    setTransactionsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/banque/transactions?entrepriseId=${entrepriseId}&banqueId=${selectedBanque.id}`);
      const data = await res.json();
      setTransactions(data);
    } catch (err) { setTransactions([]); }
    finally { setTransactionsLoading(false); }
  };

  const handleSubmitTransfert = async (e) => {
    e.preventDefault();
    const montantNum = parseFloat(montant);
    if (montantNum > soldeCaisse) {
        showCustomDialog('error', "Fonds insuffisants en caisse principale.");
        return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/banque/transfert-caisse-vers-banque`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entrepriseId,
          banqueId: selectedBanque.id,
          montant: montantNum,
          date,
          libelle: libelle || `Transfert vers ${selectedBanque.nom}`
        }),
      });
      if (res.ok) {
        showCustomDialog('success', "Transfert enregistré avec succès !");
        setShowForm(false); setMontant(''); setLibelle('');
        loadSoldesTresorerie(); loadTransactions();
      } else { throw new Error("Échec du transfert."); }
    } catch (err) { showCustomDialog('error', err.message); }
    finally { setLoading(false); }
  };

  const executeDelete = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/banque/transfert/${id}?entrepriseId=${entrepriseId}`, { method: 'DELETE' });
      if (res.ok) {
        showCustomDialog('success', "Le transfert a été annulé et les soldes mis à jour.");
        loadSoldesTresorerie(); loadTransactions();
      }
    } catch (err) { showCustomDialog('error', "Erreur lors de la suppression."); }
  };

  // --- Composant Dialogue UI ---
  const CustomDialogUI = () => {
    if (!customDialog) return null;
    const { type, message } = customDialog;
    let icon = type === 'success' ? <FaCheckCircle color="#10b981" size="3em"/> : 
               type === 'error' ? <FaTimes color="#f44336" size="3em"/> : 
               <FaExclamationTriangle color="#ffb300" size="3em"/>;

    return (
        <div className="modal-overlay">
            <div className="modal-content custom-dialog-box">
                <div className="dialog-header-icon">{icon}</div>
                <p className="dialog-msg">{message}</p>
                <div className="modal-actions centered">
                    {type === 'confirm' && <button className="btn-secondary" onClick={closeCustomDialog}>Annuler</button>}
                    <button className="btn-primary" onClick={handleDialogAction}>
                        {type === 'confirm' ? "Confirmer" : "Fermer"}
                    </button>
                </div>
            </div>
        </div>
    );
  };

  // --- FORMATAGE ---
  const formatCurrency = (amt) => Number(amt || 0).toLocaleString('fr-FR') + " FCFA";

  // --- VUE 1 : ACCUEIL (SÉLECTION BANQUE) ---
  if (!selectedBanque) {
    return (
      <div className="banque-module">
        <header className="selection-header">
            <div className="icon-badge"><FaUniversity /></div>
            <h2>Comptes Bancaires</h2>
            <p>Gérez vos différents comptes et transferts de fonds</p>
        </header>

        <div className="banques-grid">
          {banques.map(b => (
            <div key={b.id} className="banque-card-item" onClick={() => setSelectedBanque(b)}>
              <div className="card-info">
                <FaLandmark className="card-icon" />
                <div className="card-texts">
                    <span className="card-name">{b.nom}</span>
                    <span className="card-sub">Gérer la trésorerie</span>
                </div>
              </div>
              <FaChevronRight className="card-arrow" />
            </div>
          ))}
          <button className="btn-add-new-banque" onClick={() => setShowBanqueForm(true)}>
            <FaPlusCircle /> Ajouter une Banque
          </button>
        </div>

        {showBanqueForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button className="close-btn" onClick={() => setShowBanqueForm(false)}><FaTimes /></button>
              <h4>Nouvelle Banque</h4>
              <form onSubmit={handleAddBanque}>
                <label>Nom de l'établissement bancaire
                  <input type="text" value={newBanqueNom} onChange={(e) => setNewBanqueNom(e.target.value)} required placeholder="ex: BOA, Ecobank..." />
                </label>
                <button type="submit" className="btn-primary full-width" disabled={loading}>
                    {loading ? <FaSpinner className="spin" /> : "Ajouter le compte"}
                </button>
              </form>
            </div>
          </div>
        )}
        <CustomDialogUI />
      </div>
    );
  }

  // --- VUE 2 : GESTION D'UNE BANQUE ---
  return (
    <div className="banque-module">
      <div className="top-nav-bar">
        <button className="btn-nav-back" onClick={() => {setSelectedBanque(null); setViewMode('main')}}>
          <FaArrowLeft /> Liste des comptes
        </button>
      </div>

      <header className="bank-header">
        <div className="bank-title-group">
            <FaUniversity className="bank-icon-header" />
            <h3>{selectedBanque.nom}</h3>
        </div>
        <div className="menu-container" ref={menuRef}>
            <button className="menu-toggle-btn" onClick={() => setShowMenu(!showMenu)}><FaEllipsisV /></button>
            {showMenu && (
                <div className="menu-dropdown">
                    <div className="menu-item" onClick={() => { setViewMode('history'); setShowMenu(false); }}>
                        <FaHistory /> Historique des flux
                    </div>
                </div>
            )}
        </div>
      </header>

      {viewMode === 'main' ? (
        <div className="view-content">
          <div className="dashboard-soldes">
            <div className="solde-item-card caisse">
                <span className="label"><FaDollarSign /> Caisse Principale</span>
                <span className="value">{formatCurrency(soldeCaisse)}</span>
            </div>
            <div className="solde-item-card banque">
                <span className="label"><FaMoneyBillWave /> Solde {selectedBanque.nom}</span>
                <span className="value">{formatCurrency(soldeBanque)}</span>
            </div>
          </div>

          <button className="btn-big-transfert" onClick={() => setShowForm(true)}>
            <FaPlusCircle /> Nouveau Transfert (Caisse -> {selectedBanque.nom})
          </button>
        </div>
      ) : (
        <div className="history-section">
          <div className="history-controls">
            <button className="btn-back-link" onClick={() => setViewMode('main')}><FaArrowLeft /> Retour aux soldes</button>
            <h4>Journal des transactions</h4>
          </div>
          <div className="table-responsive-container">
            <table className="bank-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Libellé</th>
                  <th className="text-right">Montant</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? <tr><td colSpan="4" className="empty-row">Aucune transaction enregistrée</td></tr> : 
                  transactions.map(tx => (
                    <tr key={tx.id}>
                      <td data-label="Date">{new Date(tx.date_transaction).toLocaleDateString('fr-FR')}</td>
                      <td data-label="Libellé">{tx.libelle}</td>
                      <td data-label="Montant" className="text-right tx-positive">+{formatCurrency(tx.montant)}</td>
                      <td data-label="Actions" className="text-center">
                        <button className="btn-action-delete" onClick={() => showCustomDialog('confirm', "Voulez-vous vraiment annuler ce transfert ?", executeDelete, tx.id)}>
                            <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-btn" onClick={() => setShowForm(false)}><FaTimes /></button>
            <h4>Transférer vers {selectedBanque.nom}</h4>
            <form onSubmit={handleSubmitTransfert} className="bank-form">
              <div className="form-group">
                <label>Montant à verser (FCFA)</label>
                <input type="number" value={montant} onChange={(e) => setMontant(e.target.value)} required min="1" />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Libellé du versement</label>
                <input type="text" value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder={`Transfert vers ${selectedBanque.nom}`} />
              </div>
              <button type="submit" className="btn-primary full-width" disabled={loading}>
                {loading ? <FaSpinner className="spin" /> : "Valider le versement"}
              </button>
            </form>
          </div>
        </div>
      )}
      <CustomDialogUI />
    </div>
  );
};

export default Banque;